import React, { useState, useEffect, useCallback } from 'react';
import { ThreeDots } from 'react-loader-spinner';
import { feedbackService } from '../../services/feedbackService';

const FEEDBACK_STATUSES = ['New', 'In Progress', 'On Hold', 'Done'];

const SORT_COLUMNS = [
    { key: 'timestamp', label: 'Date' },
    { key: 'userEmail', label: 'User' },
    { key: 'rating', label: 'Rating' },
    { key: 'category', label: 'Category' },
    { key: 'status', label: 'Status' },
];

const AdminFeedbackTab = () => {
    const [feedback, setFeedback] = useState([]);
    const [loading, setLoading] = useState(false);
    const [feedbackPage, setFeedbackPage] = useState(1);
    const [feedbackTotalPages, setFeedbackTotalPages] = useState(1);
    const [feedbackFilter, setFeedbackFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [sortBy, setSortBy] = useState('timestamp');
    const [sortOrder, setSortOrder] = useState('desc');
    const [error, setError] = useState('');
    const [statusBusyId, setStatusBusyId] = useState(null);

    const loadFeedback = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const response = await feedbackService.getFeedback({
                page: feedbackPage,
                page_size: 20,
                filter: feedbackFilter,
                status: statusFilter,
                sort_by: sortBy,
                sort_order: sortOrder,
            });
            if (response && response.feedback) {
                setFeedback(response.feedback);
                setFeedbackTotalPages(response.pagination.total_pages);
            }
        } catch (err) {
            console.error('Error loading feedback:', err);
            setError('Failed to load feedback.');
        } finally {
            setLoading(false);
        }
    }, [feedbackPage, feedbackFilter, statusFilter, sortBy, sortOrder]);

    useEffect(() => {
        loadFeedback();
    }, [loadFeedback]);

    const handleCategoryFilterChange = (filter) => {
        setFeedbackFilter(filter);
        setFeedbackPage(1);
    };

    const handleStatusFilterChange = (value) => {
        setStatusFilter(value);
        setFeedbackPage(1);
    };

    const handleSort = (columnKey) => {
        setFeedbackPage(1);
        if (columnKey === sortBy) {
            setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortBy(columnKey);
            setSortOrder(columnKey === 'timestamp' ? 'desc' : 'asc');
        }
    };

    const handleFeedbackStatusChange = async (item, newStatus) => {
        const prev = item.status || 'New';
        setStatusBusyId(item.id);
        setFeedback((rows) =>
            rows.map((r) => (r.id === item.id ? { ...r, status: newStatus } : r)),
        );
        try {
            await feedbackService.updateFeedbackStatus({ id: item.id, status: newStatus });
        } catch (err) {
            console.error('Error updating feedback status:', err);
            setFeedback((rows) =>
                rows.map((r) => (r.id === item.id ? { ...r, status: prev } : r)),
            );
            setError('Failed to update status. Try again.');
        } finally {
            setStatusBusyId(null);
        }
    };

    const formatDate = (dateString) =>
        new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });

    const sortIndicator = (key) => {
        if (sortBy !== key) return null;
        return <span className="admin-feedback-sort-indicator">{sortOrder === 'asc' ? '▲' : '▼'}</span>;
    };

    const ariaSortFor = (key) => {
        if (sortBy !== key) return 'none';
        return sortOrder === 'asc' ? 'ascending' : 'descending';
    };

    return (
        <div>
            <h2 className="admin-section-title">User Feedback</h2>

            <div className="feedback-controls admin-feedback-controls admin-feedback-toolbar">
                <div className="admin-feedback-toolbar-row">
                    <label className="admin-feedback-filter-label" htmlFor="admin-feedback-category">
                        Category / rating
                    </label>
                    <select
                        id="admin-feedback-category"
                        value={feedbackFilter}
                        onChange={(e) => handleCategoryFilterChange(e.target.value)}
                        className="feedback-filter-select"
                    >
                        <option value="all">All Feedback</option>
                        <option value="positive">Positive</option>
                        <option value="negative">Negative</option>
                        <option value="Bug Report">Bug Reports</option>
                        <option value="Feature Request">Feature Requests</option>
                        <option value="User Experience">User Experience</option>
                        <option value="Performance">Performance</option>
                        <option value="Content Quality">Content Quality</option>
                        <option value="General Feedback">General Feedback</option>
                    </select>
                </div>
                <div className="admin-feedback-toolbar-row">
                    <label className="admin-feedback-filter-label" htmlFor="admin-feedback-status-filter">
                        Status
                    </label>
                    <select
                        id="admin-feedback-status-filter"
                        value={statusFilter}
                        onChange={(e) => handleStatusFilterChange(e.target.value)}
                        className="feedback-filter-select"
                    >
                        <option value="all">All statuses</option>
                        {FEEDBACK_STATUSES.map((s) => (
                            <option key={s} value={s}>
                                {s}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {error && <div className="admin-empty">{error}</div>}

            {loading ? (
                <div className="loader-container">
                    <ThreeDots color="#00BFFF" height={40} width={40} />
                </div>
            ) : (
                <>
                    <div className="admin-table-wrapper admin-feedback-table">
                        {feedback.length > 0 ? (
                            <table>
                                <thead>
                                    <tr>
                                        {SORT_COLUMNS.map(({ key, label }) => (
                                            <th
                                                key={key}
                                                className="admin-feedback-th-sortable"
                                                aria-sort={ariaSortFor(key)}
                                            >
                                                <button
                                                    type="button"
                                                    className="admin-feedback-sort-button"
                                                    onClick={() => handleSort(key)}
                                                >
                                                    {label}
                                                    {sortIndicator(key)}
                                                </button>
                                            </th>
                                        ))}
                                        <th>Feedback</th>
                                        <th>Context</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {feedback.map((item) => (
                                        <tr key={item.id}>
                                            <td>{formatDate(item.timestamp)}</td>
                                            <td>
                                                {item.isAnonymous ? (
                                                    <span className="anonymous-badge">Anonymous</span>
                                                ) : (
                                                    <span className="user-email">{item.userEmail}</span>
                                                )}
                                            </td>
                                            <td className="admin-feedback-td-rating">
                                                <span className={`rating-badge ${item.rating}`}>
                                                    {item.rating === 'positive' ? '👍 Good' : '👎 Not Good'}
                                                </span>
                                            </td>
                                            <td className="admin-feedback-td-category">
                                                <span className="category-badge">{item.category}</span>
                                            </td>
                                            <td className="admin-feedback-td-status">
                                                <select
                                                    className="admin-feedback-status-select"
                                                    value={item.status || 'New'}
                                                    disabled={statusBusyId === item.id}
                                                    onChange={(e) =>
                                                        handleFeedbackStatusChange(item, e.target.value)
                                                    }
                                                    aria-label={`Status for ${item.id}`}
                                                >
                                                    {FEEDBACK_STATUSES.map((s) => (
                                                        <option key={s} value={s}>
                                                            {s}
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td>
                                                {item.text ? (
                                                    <div title={item.text}>
                                                        {item.text.length > 100
                                                            ? `${item.text.substring(0, 100)}...`
                                                            : item.text}
                                                    </div>
                                                ) : (
                                                    <span className="no-text">No additional comments</span>
                                                )}
                                            </td>
                                            <td>
                                                <span title={item.url}>
                                                    {item.context || 'Unknown'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="admin-empty">
                                {feedbackFilter === 'all' && statusFilter === 'all'
                                    ? 'No feedback submitted yet.'
                                    : 'No feedback matches the current filters.'}
                            </div>
                        )}
                    </div>

                    {feedbackTotalPages > 1 && (
                        <div className="pagination admin-pagination-spaced">
                            <button
                                type="button"
                                onClick={() => setFeedbackPage((p) => Math.max(1, p - 1))}
                                disabled={feedbackPage === 1}
                                className="pagination-button"
                            >
                                Previous
                            </button>
                            <span className="pagination-info">
                                Page {feedbackPage} of {feedbackTotalPages}
                            </span>
                            <button
                                type="button"
                                onClick={() =>
                                    setFeedbackPage((p) => Math.min(feedbackTotalPages, p + 1))
                                }
                                disabled={feedbackPage === feedbackTotalPages}
                                className="pagination-button"
                            >
                                Next
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default AdminFeedbackTab;
